/**
 * matroska-subs.js — window.MatroskaSubs
 *
 * Read the subtitle tracks of a Matroska/WebM file in JavaScript, without
 * booting FFmpeg.
 *
 * WHY. /video-player auto-picks an embedded subtitle track the moment a file
 * opens. That went through FFmpeg (`-map 0:s:N -c:s webvtt`), which has to
 * demux the WHOLE file: measured 19–24 s on an 820 MB / 24-minute MKV to
 * produce 3 KB of text. Two costs came out of that, and both were paid by the
 * viewer:
 *   · subtitles appeared ~20 s after the picture;
 *   · FFmpegProcessor is ONE worker with no queue, so for those ~20 s any
 *     action the viewer took that needs the engine — switching the audio
 *     track, above all — simply queued behind speculative work nobody asked
 *     for (measured 23 s for the first switch after opening a big file).
 *
 * The 20 s is not I/O. Reading the same 783 MB file through `file.slice()` in
 * this browser takes **0.56–0.71 s** (~1.1–1.4 GB/s). It is FFmpeg-WASM demux
 * overhead, ~35x the cost of simply looking at the bytes. So for the container
 * where embedded subtitles actually live, walk it here instead.
 *
 * WHAT IT DOES. Streams the file once, parses EBML, and rebuilds the ORIGINAL
 * subtitle file (SRT for S_TEXT/UTF8, ASS/SSA reconstructed from CodecPrivate
 * + the Dialogue fields Matroska stores, WebVTT for S_TEXT/WEBVTT) — the same
 * artefact `-c:s copy` would produce, so it hands off to the existing
 * SubtitleParser and nothing downstream changes.
 *
 * WHAT IT DOES NOT DO, on purpose:
 *   · picture subtitles (PGS / VobSub / DVB) — they are images, there is
 *     nothing to turn into text without OCR, and the tool already says so;
 *   · any container that is not Matroska — MP4/MOV/AVI/TS keep the FFmpeg
 *     path, which is why that path stays and got windowed rather than removed.
 * Both are reported as a miss, never as "this file has no subtitles": a reader
 * that cannot tell "I did not look" from "there is nothing there" is the exact
 * defect class this tool keeps finding.
 *
 *   const r = await MatroskaSubs.read(file, { onProgress(p){}, signal });
 *   // r = { ok, tracks:[{relIndex, number, codec, lang, name, isDefault,
 *   //                    text, ext, cues}], scannedBytes, ms }
 */
(function () {
'use strict';
if (typeof window === 'undefined') return;

var ID_SEGMENT      = 0x18538067;
var ID_INFO         = 0x1549A966;
var ID_TIMECODESCALE= 0x2AD7B1;
var ID_DURATION     = 0x4489;
var ID_TRACKS       = 0x1654AE6B;
var ID_TRACKENTRY   = 0xAE;
var ID_TRACKNUMBER  = 0xD7;
var ID_TRACKTYPE    = 0x83;
var ID_CODECID      = 0x86;
var ID_CODECPRIVATE = 0x63A2;
var ID_LANGUAGE     = 0x22B59C;
var ID_LANGUAGE_BCP = 0x22B59D;
var ID_NAME         = 0x536E;
var ID_FLAGDEFAULT  = 0x88;
var ID_CLUSTER      = 0x1F43B675;
var ID_ATTACHMENTS  = 0x1941A469;
var ID_ATTACHEDFILE = 0x61A7;
var ID_FILENAME     = 0x466E;
var ID_FILEMIME     = 0x4660;
var ID_FILEDATA     = 0x465C;
var ID_TIMECODE     = 0xE7;
var ID_SIMPLEBLOCK  = 0xA3;
var ID_BLOCKGROUP   = 0xA0;
var ID_BLOCK        = 0xA1;
var ID_BLOCKDURATION= 0x9B;

var TRACK_SUBTITLE = 0x11;

/* Text subtitle codecs. Everything else in a subtitle track is a PICTURE
   (PGS/VobSub/DVB) and is deliberately not our business — see the header. */
var TEXT_CODEC = {
    'S_TEXT/UTF8': 'srt',
    'S_TEXT/ASCII': 'srt',
    'S_TEXT/USF': '',
    'S_TEXT/SSA': 'ass',
    'S_TEXT/ASS': 'ass',
    'S_SSA': 'ass',
    'S_ASS': 'ass',
    'S_TEXT/WEBVTT': 'vtt',
    /* WebM does not use the S_ names for WebVTT — it has its own D_ ids, and a
       reader that only knows S_TEXT/WEBVTT reports a WebM caption track as
       "no text subtitles" (measured on a real ffmpeg-muxed .webm). */
    'D_WEBVTT/SUBTITLES': 'webvtt-webm',
    'D_WEBVTT/CAPTIONS': 'webvtt-webm',
    'D_WEBVTT/DESCRIPTIONS': 'webvtt-webm',
    'D_WEBVTT/METADATA': ''
};

/* A D_WEBVTT block carries two metadata fields before the text (cue settings
   and cue identifier — the WebM container spec). Both are DROPPED rather than
   ordered, because dropping makes their order irrelevant, and the order cannot
   be established from any file this build can produce: ffmpeg's webvtt-in-webm
   muxer writes both empty even when the source .vtt has an identifier and
   settings (verified — every block payload begins with two empty lines).
   Keeping them would put "CUEID1" on screen as a line of dialogue. */
var NL = String.fromCharCode(10);
function webvttBody(payload) {
    var a = payload.indexOf(NL);
    if (a < 0) return payload;
    var b = payload.indexOf(NL, a + 1);
    if (b < 0) return payload;
    var body = payload.slice(b + 1);
    return body.length ? body : payload;
}

/* ── a forward-only byte window over the File ─────────────────────────────
   The file is read in chunks and never held whole: a 4 GB rip must cost the
   same as a 400 MB one. `need(n)` guarantees n bytes are available at the
   cursor, pulling more from disk when they are not. */
function Reader(file, chunkSize) {
    this.file = file;
    this.size = file.size;
    this.chunk = chunkSize || 4 * 1024 * 1024;
    this.buf = new Uint8Array(0);
    this.bufStart = 0;      // absolute offset of buf[0]
    this.pos = 0;           // absolute cursor
    this.read = 0;          // bytes actually pulled from the file
}
Reader.prototype.need = async function (n) {
    /* A cursor BEHIND the window must refill, not be believed: the arithmetic
       alone stays positive there and at() would index the array negatively,
       returning undefined bytes that parse as a plausible element. */
    var have = (this.pos >= this.bufStart) ? (this.bufStart + this.buf.length - this.pos) : -1;
    if (have >= n) return true;
    if (this.pos >= this.size) return false;
    var want = Math.max(this.chunk, n);
    var from = this.pos;
    var to = Math.min(this.size, from + want);
    var next = new Uint8Array(await this.file.slice(from, to).arrayBuffer());
    this.read += next.length;
    this.buf = next;
    this.bufStart = from;
    return (this.buf.length >= n);
};
Reader.prototype.at = function (i) { return this.buf[this.pos - this.bufStart + i]; };
Reader.prototype.slice = function (n) {
    var o = this.pos - this.bufStart;
    return this.buf.subarray(o, o + n);
};
Reader.prototype.skip = function (n) { this.pos += n; };
Reader.prototype.seek = function (p) { this.pos = p; };
Reader.prototype.eof = function () { return this.pos >= this.size; };

/* EBML element id: the leading-1 bit of the first byte gives the length, and
   the id KEEPS its marker bits (unlike a size vint). */
async function readId(r) {
    if (!(await r.need(1))) return -1;
    var b0 = r.at(0), len = 0;
    if (b0 & 0x80) len = 1; else if (b0 & 0x40) len = 2; else if (b0 & 0x20) len = 3; else if (b0 & 0x10) len = 4;
    else return -1;                                   // not a valid id here
    if (!(await r.need(len))) return -1;
    var v = 0;
    for (var i = 0; i < len; i++) v = v * 256 + r.at(i);
    r.skip(len);
    return v;
}
/* Size vint: marker bit stripped. All-ones means "unknown size" (live/streamed
   Segments and Clusters really do use it) — reported as -1 so callers descend
   instead of trusting a bogus length. */
async function readSize(r) {
    if (!(await r.need(1))) return -2;
    var b0 = r.at(0), len = 0, mask = 0;
    for (var i = 0; i < 8; i++) { if (b0 & (0x80 >> i)) { len = i + 1; mask = (0x80 >> i) ^ 0xFF; break; } }
    if (!len) return -2;
    if (!(await r.need(len))) return -2;
    var v = r.at(0) & mask, allOnes = (r.at(0) & mask) === mask;
    for (var j = 1; j < len; j++) { v = v * 256 + r.at(j); if (r.at(j) !== 0xFF) allOnes = false; }
    r.skip(len);
    if (allOnes) return -1;
    return v;
}
function uintOf(bytes) { var v = 0; for (var i = 0; i < bytes.length; i++) v = v * 256 + bytes[i]; return v; }
function floatOf(bytes) {
    var dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (bytes.length === 4) return dv.getFloat32(0);
    if (bytes.length === 8) return dv.getFloat64(0);
    return uintOf(bytes);
}
function utf8Of(bytes) {
    try { return new TextDecoder('utf-8').decode(bytes); }
    catch (_) { var s = ''; for (var i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]); return s; }
}
/* Block header: track number (vint), 16-bit signed relative timecode, flags. */
function parseBlockHeader(b) {
    var b0 = b[0], len = 0, mask = 0;
    for (var i = 0; i < 8; i++) { if (b0 & (0x80 >> i)) { len = i + 1; mask = (0x80 >> i) ^ 0xFF; break; } }
    if (!len || b.length < len + 3) return null;
    var track = b[0] & mask;
    for (var j = 1; j < len; j++) track = track * 256 + b[j];
    var rel = (b[len] << 8) | b[len + 1];
    if (rel & 0x8000) rel -= 0x10000;
    return { track: track, rel: rel, payloadAt: len + 3 };
}

function pad(n, w) { var s = String(n); while (s.length < w) s = '0' + s; return s; }
function srtTime(t) {
    if (!(t >= 0)) t = 0;
    /* Whole milliseconds first. Carrying a rounded-up 1000 ms into the seconds
       alone turned 59.9996 s into "00:00:60,000" — an invalid timing a player
       drops together with its cue. */
    var total = Math.round(t * 1000);
    var h = Math.floor(total / 3600000), m = Math.floor(total % 3600000 / 60000), s = Math.floor(total % 60000 / 1000), ms = total % 1000;
    return pad(h, 2) + ':' + pad(m, 2) + ':' + pad(s, 2) + ',' + pad(ms, 3);
}
function vttTime(t) { return srtTime(t).replace(',', '.'); }
function assTime(t) {
    if (!(t >= 0)) t = 0;
    var h = Math.floor(t / 3600), m = Math.floor(t % 3600 / 60), s = t % 60;
    var cs = Math.round(s * 100) / 100;
    return h + ':' + pad(m, 2) + ':' + (cs < 10 ? '0' : '') + cs.toFixed(2);
}

/* Matroska stores an ASS event as the Dialogue FIELDS with ReadOrder first and
   the timestamps removed (they live in the block). Put the line back together
   in the order the [Events] Format line promises. */
function assDialogue(payload, start, end) {
    var parts = payload.split(',');
    var layer = '0', rest = payload;
    if (parts.length >= 9) {
        layer = parts[1] || '0';
        rest = parts.slice(2).join(',');
    } else {
        rest = payload;
    }
    return 'Dialogue: ' + layer + ',' + assTime(start) + ',' + assTime(end) + ',' + rest;
}

async function openSegment(file, chunkSize) {
    var r = new Reader(file, chunkSize);
    if (!(await r.need(4)) || r.at(0) !== 0x1A || r.at(1) !== 0x45 || r.at(2) !== 0xDF || r.at(3) !== 0xA3) return null;
    await readId(r);
    var hdrSize = await readSize(r);
    if (hdrSize >= 0) r.skip(hdrSize);
    for (;;) {
        if (r.eof()) return null;
        var id = await readId(r);
        if (id === -1) return null;
        var sz = await readSize(r);
        if (sz === -2) return null;
        if (id === ID_SEGMENT) {
            /* `end` is where we can actually read to; `declaredEnd` is where the
               container SAYS it ends. They differ exactly when the file was cut
               short, and only the second one can prove that — clamping first and
               comparing against the clamp always says "complete". */
            return { r: r, end: (sz >= 0) ? Math.min(r.size, r.pos + sz) : r.size,
                     declaredEnd: (sz >= 0) ? (r.pos + sz) : -1 };
        }
        if (sz < 0) return null;
        r.skip(sz);
    }
}

/* ── pass 1: the header ───────────────────────────────────────────────────
   Walk the top level and read only Info and Tracks, SEEKING PAST every
   Cluster body. This exists because element order is not guaranteed: a file
   muxed in one pass can put Tracks AFTER the clusters (media-audio-probe.js
   already carries that scar — "Tracks can trail the Clusters"). A single
   forward walk that met the clusters first would collect blocks for tracks it
   had not learned about yet and then report "this file has no subtitles" — a
   silent wrong answer, which is worse than being slow. Skipping cluster
   bodies costs one small read per cluster, so the ordering stops mattering. */
async function readHeader(file) {
    var seg = await openSegment(file, 64 * 1024);
    if (!seg) return { ok: false, reason: 'not_matroska', read: 0 };
    var r = seg.r;
    var scale = 1000000, duration = 0, subs = [], relIndex = 0, attachments = [];
    while (r.pos < seg.end) {
        if (!(await r.need(1))) break;
        var id = await readId(r); if (id === -1) break;
        var sz = await readSize(r); if (sz === -2) break;
        var elStart = r.pos;

        if (id === ID_INFO && sz >= 0) {
            var infoEnd = elStart + sz;
            while (r.pos < infoEnd) {
                if (!(await r.need(1))) break;
                var iid = await readId(r); if (iid === -1) break;
                var isz = await readSize(r); if (isz < 0) break;
                if (!(await r.need(isz))) break;
                if (iid === ID_TIMECODESCALE) scale = uintOf(r.slice(isz)) || scale;
                else if (iid === ID_DURATION) duration = floatOf(r.slice(isz));
                r.skip(isz);
            }
            r.seek(infoEnd);
            continue;
        }
        if (id === ID_TRACKS && sz >= 0) {
            var tEnd = elStart + sz;
            while (r.pos < tEnd) {
                if (!(await r.need(1))) break;
                var tid = await readId(r); if (tid === -1) break;
                var tsz = await readSize(r); if (tsz < 0) break;
                if (tid !== ID_TRACKENTRY) { r.skip(tsz); continue; }
                var eEnd = r.pos + tsz;
                var e = { number: 0, type: 0, codec: '', priv: null, lang: '', name: '', isDefault: false };
                while (r.pos < eEnd) {
                    if (!(await r.need(1))) break;
                    var fid = await readId(r); if (fid === -1) break;
                    var fsz = await readSize(r); if (fsz < 0) break;
                    if (!(await r.need(fsz))) break;
                    var v = r.slice(fsz);
                    if (fid === ID_TRACKNUMBER) e.number = uintOf(v);
                    else if (fid === ID_TRACKTYPE) e.type = uintOf(v);
                    else if (fid === ID_CODECID) e.codec = utf8Of(v);
                    else if (fid === ID_CODECPRIVATE) e.priv = utf8Of(v);
                    else if (fid === ID_LANGUAGE || fid === ID_LANGUAGE_BCP) e.lang = e.lang || utf8Of(v);
                    else if (fid === ID_NAME) e.name = utf8Of(v);
                    else if (fid === ID_FLAGDEFAULT) e.isDefault = uintOf(v) === 1;
                    r.skip(fsz);
                }
                r.seek(eEnd);
                if (e.type === TRACK_SUBTITLE) {
                    subs.push({ relIndex: relIndex++, number: e.number, codec: e.codec,
                                kind: TEXT_CODEC[String(e.codec).toUpperCase()] || '',
                                lang: e.lang || 'und', name: e.name, isDefault: e.isDefault,
                                priv: e.priv, events: [] });
                }
            }
            r.seek(tEnd);
            continue;
        }
        /* Attachments: where each file's bytes sit, not the bytes — an ASS
           release ships its fonts here, and they are read only if the viewer
           picks an ASS track (fonts()). */
        if (id === ID_ATTACHMENTS && sz >= 0) {
            var aEnd = elStart + sz;
            while (r.pos < aEnd) {
                if (!(await r.need(1))) break;
                var fid0 = await readId(r); if (fid0 === -1) break;
                var fsz0 = await readSize(r); if (fsz0 < 0) break;
                if (fid0 !== ID_ATTACHEDFILE) { r.skip(fsz0); continue; }
                var fEnd = r.pos + fsz0, att = { name: '', mime: '', at: -1, size: 0 };
                while (r.pos < fEnd) {
                    if (!(await r.need(1))) break;
                    var aid = await readId(r); if (aid === -1) break;
                    var asz = await readSize(r); if (asz < 0) break;
                    if (aid === ID_FILEDATA) { att.at = r.pos; att.size = asz; r.skip(asz); continue; }
                    if (asz > 4096) { r.skip(asz); continue; }
                    if (!(await r.need(asz))) break;
                    if (aid === ID_FILENAME) att.name = utf8Of(r.slice(asz));
                    else if (aid === ID_FILEMIME) att.mime = utf8Of(r.slice(asz));
                    r.skip(asz);
                }
                r.seek(fEnd);
                if (att.at >= 0 && att.size > 0) attachments.push(att);
            }
            r.seek(aEnd);
            continue;
        }
        if (sz < 0) break;              // unknown-size non-cluster: cannot skip safely
        r.skip(sz);                     // Cluster bodies land here — seek, never read
    }
    return { ok: true, scale: scale, duration: duration * scale / 1e9, subs: subs, read: r.read, segEnd: seg.end, attachments: attachments };
}

/* ── pass 2: the blocks ───────────────────────────────────────────────────
   Only runs when pass 1 found a TEXT subtitle track, so a file with none (or
   with picture subtitles only) never pays for a full walk. */
async function readBlocks(file, hdr, opts) {
    var seg = await openSegment(file, opts.chunkSize);
    if (!seg) return 0;
    var r = seg.r;
    var byNumber = {};
    for (var i = 0; i < hdr.subs.length; i++) if (hdr.subs[i].kind) byNumber[hdr.subs[i].number] = hdr.subs[i];
    var scale = hdr.scale, clusterTime = 0, lastReport = 0;

    function idLen(id) { return id > 0xFFFFFF ? 4 : id > 0xFFFF ? 3 : id > 0xFF ? 2 : 1; }
    function collectBlock(bytes, durTicks) {
        var h = parseBlockHeader(bytes);
        if (!h) return;
        var d = byNumber[h.track];
        if (!d) return;
        var start = (clusterTime + h.rel) * scale / 1e9;
        var raw = utf8Of(bytes.subarray(h.payloadAt));
        d.events.push({ start: start, end: start + (durTicks ? durTicks * scale / 1e9 : 0),
                        text: d.kind === 'webvtt-webm' ? webvttBody(raw) : raw });
    }

    while (r.pos < seg.end) {
        if (opts.signal && opts.signal.aborted) return -1;
        if (!(await r.need(1))) break;
        var id = await readId(r); if (id === -1) break;
        var sz = await readSize(r); if (sz === -2) break;
        var elStart = r.pos;
        if (id !== ID_CLUSTER) { if (sz < 0) break; r.skip(sz); continue; }

        var cEnd = (sz >= 0) ? elStart + sz : seg.end;
        while (r.pos < cEnd) {
            if (opts.signal && opts.signal.aborted) return -1;
            if (!(await r.need(1))) break;
            var cid = await readId(r);
            if (cid === -1) break;
            /* An unknown-size Cluster ends where the next top-level element
               begins; those ids are not legal children, so meeting one means
               we have walked out of this cluster. */
            if (sz < 0 && (cid === ID_CLUSTER || cid === ID_TRACKS || cid === ID_INFO)) { r.skip(-idLen(cid)); break; }
            var csz = await readSize(r);
            if (csz < 0) break;
            if (cid === ID_TIMECODE) {
                if (!(await r.need(csz))) break;
                clusterTime = uintOf(r.slice(csz)); r.skip(csz); continue;
            }
            if (cid === ID_SIMPLEBLOCK || cid === ID_BLOCK) {
                if (!(await r.need(csz))) break;
                collectBlock(r.slice(csz), 0); r.skip(csz); continue;
            }
            if (cid === ID_BLOCKGROUP) {
                var gEnd = r.pos + csz, blockBytes = null, blockDur = 0;
                while (r.pos < gEnd) {
                    if (!(await r.need(1))) break;
                    var gid = await readId(r); if (gid === -1) break;
                    var gsz = await readSize(r); if (gsz < 0) break;
                    if (!(await r.need(gsz))) break;
                    if (gid === ID_BLOCK) blockBytes = new Uint8Array(r.slice(gsz));
                    else if (gid === ID_BLOCKDURATION) blockDur = uintOf(r.slice(gsz));
                    r.skip(gsz);
                }
                r.seek(gEnd);
                if (blockBytes) collectBlock(blockBytes, blockDur);
                continue;
            }
            r.skip(csz);
        }
        if (sz >= 0) r.seek(cEnd);
        if (opts.onProgress && (r.pos - lastReport) > 32 * 1024 * 1024) {
            lastReport = r.pos;
            try { opts.onProgress(Math.min(1, r.pos / seg.end)); } catch (_) {}
        }
    }
    /* Stopping short of the segment means the file ends mid-stream (an
       interrupted download, a half-copied rip). The cues found are real and
       worth showing, but the caller must be able to tell a COMPLETE list from
       a truncated one — silently returning the short list is the same silent
       truncation this codebase has been bitten by before. */
    hdr.partial = (seg.declaredEnd > 0 && r.pos < seg.declaredEnd - 1024);
    return r.read;
}

async function read(file, opts) {
    opts = opts || {};
    var t0 = (window.performance || Date).now();
    var out = { ok: false, tracks: [], scannedBytes: 0, ms: 0, reason: '' };
    if (!file || !file.size) { out.reason = 'no_file'; return out; }

    var hdr = await readHeader(file);
    if (!hdr.ok) { out.reason = hdr.reason || 'bad_ebml'; out.ms = Math.round((window.performance || Date).now() - t0); return out; }
    out.duration = hdr.duration || 0;
    out.scannedBytes = hdr.read;
    out.headerMs = Math.round((window.performance || Date).now() - t0);

    var hasText = false;
    for (var q = 0; q < hdr.subs.length; q++) if (hdr.subs[q].kind) hasText = true;
    if (hasText) {
        var got = await readBlocks(file, hdr, opts);
        if (got === -1) { out.reason = 'aborted'; out.ms = Math.round((window.performance || Date).now() - t0); return out; }
        out.scannedBytes += got;
        out.partial = !!hdr.partial;
    } else if (!hdr.subs.length) {
        out.reason = 'no_subtitle_tracks';
    } else {
        /* Picture subtitles only. A MISS, never an absence: the tool has to be
           able to say "PGS — these are images" instead of "no subtitles". */
        out.reason = 'no_text_tracks';
    }

    out.ok = true;
    out.ms = Math.round((window.performance || Date).now() - t0);
    out.tracks = hdr.subs.map(function (d) {
        var t = { relIndex: d.relIndex, number: d.number, codec: d.codec, kind: d.kind,
                  lang: d.lang, name: d.name, isDefault: d.isDefault, cues: d.events.length, text: '', ext: '' };
        if (!d.kind || !d.events.length) return t;
        d.events.sort(function (a, b) { return a.start - b.start; });
        /* A block with no BlockDuration (some muxers write subtitles as
           SimpleBlock, which cannot carry one) has no end. Run it to the next
           cue rather than dropping it or leaving it on screen forever. */
        for (var i = 0; i < d.events.length; i++) {
            var ev = d.events[i];
            if (ev.end > ev.start) continue;
            ev.end = (i + 1 < d.events.length) ? Math.min(d.events[i + 1].start, ev.start + 8) : ev.start + 4;
        }
        if (d.kind === 'srt') {
            var lines = [];
            for (var j = 0; j < d.events.length; j++) {
                lines.push((j + 1) + '\n' + srtTime(d.events[j].start) + ' --> ' + srtTime(d.events[j].end) + '\n' + d.events[j].text + '\n');
            }
            t.text = lines.join('\n'); t.ext = 'srt';
        } else if (d.kind === 'vtt' || d.kind === 'webvtt-webm') {
            var v = ['WEBVTT', ''];
            for (var k = 0; k < d.events.length; k++) {
                v.push(vttTime(d.events[k].start) + ' --> ' + vttTime(d.events[k].end));
                v.push(d.events[k].text); v.push('');
            }
            t.text = v.join('\n'); t.ext = 'vtt';
        } else if (d.kind === 'ass') {
            var head = d.priv || '[Script Info]\nScriptType: v4.00+\n\n[Events]\nFormat: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text\n';
            if (!/\[Events\]/i.test(head)) head += '\n[Events]\nFormat: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text\n';
            var ev2 = [];
            for (var m = 0; m < d.events.length; m++) ev2.push(assDialogue(d.events[m].text, d.events[m].start, d.events[m].end));
            t.text = head.replace(/\s*$/, '\n') + ev2.join('\n') + '\n';
            t.ext = 'ass';
        }
        return t;
    });
    return out;
}

/* The fonts a release ships for its ASS track (TrueType / OpenType / TTC,
   by MIME type or by name — muxers disagree on the MIME). Bounded: a font
   pack past FONTS_MAX_BYTES is taken as far as it fits, the rest dropped. */
var FONTS_MAX_BYTES = 64 * 1024 * 1024;
var FONT_MIME = /(font|truetype|opentype|sfnt)/i;
var FONT_EXT = /\.(ttf|otf|ttc|otc)$/i;
async function fonts(file) {
    var out = { ok: false, fonts: [], names: [], skipped: 0, ms: 0 };
    var t0 = (window.performance || Date).now();
    if (!file || !file.size) return out;
    var hdr = await readHeader(file);
    if (!hdr.ok) return out;
    var total = 0;
    for (var i = 0; i < hdr.attachments.length; i++) {
        var a = hdr.attachments[i];
        if (!FONT_MIME.test(a.mime) && !FONT_EXT.test(a.name)) continue;
        if (total + a.size > FONTS_MAX_BYTES) { out.skipped++; continue; }
        try {
            out.fonts.push(new Uint8Array(await file.slice(a.at, a.at + a.size).arrayBuffer()));
            out.names.push(a.name);
            total += a.size;
        } catch (_) { out.skipped++; }
    }
    out.ok = true;
    out.bytes = total;
    out.ms = Math.round((window.performance || Date).now() - t0);
    return out;
}

window.MatroskaSubs = { read: read, fonts: fonts, __isStub: false };
})();
